import React, { useCallback, useMemo, useState } from 'react'
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid'
import { CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { DetailButton } from '../../../components/common/detail'
import { formatTableDate } from '../../../utils/formatDate'
import { toNumber } from '../../../utils/formatNumbers'
import { useNumberFormat } from '../../../hooks/useNumberFormat'
import NumberEditCell from '../../../components/common/NumberEditCell'
import { useProjectBudgets } from '../../../components/quickCreate/parentInfo'
import { TypeAction, TypeActionTranslations, status as STATUS_VALUES } from '../../../utils/constants'
import BulkEditPanel from './BulkEditPanel'
import RowEditPanel from './RowEditPanel'
import { ImportRow, RowErrors, RowField, parseDateValue, toIsoDay, ImportContext } from './importRowModel'
import { ExtractionState } from './useImportWizard'

type Filter = 'all' | 'valid' | 'errors'

type ReviewStepProps = {
  extraction: ExtractionState
  rows: ImportRow[]
  errorsById: Record<string, RowErrors>
  labels: Record<string, string>
  deletedCount: number
  onRetryExtract: () => void
  onUpdateRow: (id: string, patch: Partial<ImportRow>) => void
  onPatchRows: (ids: string[], patch: Partial<ImportRow>) => void
  onDeleteRows: (ids: string[]) => void
  onUndoDelete: () => void
  onAddRow: () => void
  onLabels: (labels: Record<string, string>) => void
  lockMission?: boolean
  lockProject?: boolean
  preset?: ImportContext
}

/** Grid column that shows each validation field. */
const FIELD_COLUMN: Record<RowField, string> = {
  ActionName: 'ActionName',
  Description: 'Description',
  DateDebut: 'DateDebut',
  DateFin: 'DateFin',
  Cout: 'Cout',
  Priority: 'Priority',
  TypeAction: 'TypeAction',
  Status: 'Status',
  MissionId: 'MissionId',
  ProjetId: 'ProjetId',
  Sources: 'ProjetId',
  SecurityIds: 'Security',
  SecurityControlIds: 'Security',
  SubDomainIds: 'Security',
  server: 'ActionName',
}

const COLUMN_FIELDS = Object.entries(FIELD_COLUMN).reduce<Record<string, RowField[]>>(
  (acc, [field, column]) => {
    ;(acc[column] = acc[column] || []).push(field as RowField)
    return acc
  },
  {}
)

const PAGE_SIZES = [25, 50, 100]

export default function ReviewStep({
  extraction,
  rows,
  errorsById,
  labels,
  deletedCount,
  onRetryExtract,
  onUpdateRow,
  onPatchRows,
  onDeleteRows,
  onUndoDelete,
  onAddRow,
  onLabels,
  lockMission,
  lockProject,
  preset,
}: ReviewStepProps) {
  const { t, i18n } = useTranslation()
  const apiRef = useGridApiRef()
  const fmt = useNumberFormat()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [selection, setSelection] = useState<GridRowSelectionModel>([])
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [jumpedId, setJumpedId] = useState<string | null>(null)

  const errorCount = useMemo(
    () => rows.filter((r) => Object.keys(errorsById[r.id] || {}).length).length,
    [rows, errorsById]
  )

  const budgets = useProjectBudgets(rows.map((r) => r.ProjetId))
  const budgetOverruns = useMemo(() => {
    if (!budgets.data) return []
    const planned = rows.reduce<Record<string, number>>((acc, row) => {
      if (row.ProjetId) acc[row.ProjetId] = (acc[row.ProjetId] ?? 0) + (toNumber(row.Cout) ?? 0)
      return acc
    }, {})
    return Object.entries(planned)
      .filter(([id, total]) => budgets.data?.[id] && total > budgets.data[id].remaining)
      .map(([id, total]) => ({ id, total, info: budgets.data![id] }))
  }, [rows, budgets.data])

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((row) => {
      const hasErrors = Object.keys(errorsById[row.id] || {}).length > 0
      if (filter === 'valid' && hasErrors) return false
      if (filter === 'errors' && !hasErrors) return false
      if (!q) return true
      return [
        row.ActionName,
        row.Description,
        labels[row.MissionId],
        labels[row.ProjetId],
        row.sourceLine ? String(row.sourceLine) : '',
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    })
  }, [rows, errorsById, filter, search, labels])

  const errorText = useCallback(
    (row: ImportRow, column: string) =>
      (COLUMN_FIELDS[column] || [])
        .map((f) => errorsById[row.id]?.[f])
        .filter(Boolean)
        .map((e) => t(e!.key, e!.values))
        .join(' · '),
    [errorsById, t]
  )

  const cell = useCallback(
    (column: string, render: (row: ImportRow) => React.ReactNode) =>
      (params: GridRenderCellParams<ImportRow>) => {
        const message = errorText(params.row, column)
        return (
          <span className={`action-import-cell${message ? ' is-error' : ''}`} title={message || undefined}>
            {render(params.row)}
          </span>
        )
      },
    [errorText]
  )

  const empty = <span className="action-import-cell-empty">—</span>
  const labelOf = (id?: string) => (id ? labels[id] || '…' : null)

  const columns = useMemo<GridColDef<ImportRow>[]>(
    () => [
      {
        field: '__status',
        headerName: '#',
        width: 70,
        sortable: false,
        renderCell: ({ row }) => {
          const issues = Object.keys(errorsById[row.id] || {}).length
          return (
            <span
              className={`action-import-line${issues ? ' is-error' : ' is-ok'}`}
              title={issues ? t('actionImport.review.rowIssues', { count: issues }) : t('actionImport.review.rowReady')}
            >
              <span className="action-import-dot" aria-hidden />
              {row.sourceLine ?? '+'}
            </span>
          )
        },
      },
      {
        field: 'ActionName',
        headerName: t('actionCreate.sections.identification.taskName'),
        flex: 1.4,
        minWidth: 200,
        editable: true,
        renderCell: cell('ActionName', (r) => r.ActionName || empty),
      },
      {
        field: 'Description',
        headerName: t('Description'),
        flex: 1.6,
        minWidth: 220,
        editable: true,
        renderCell: cell('Description', (r) => r.Description || empty),
      },
      ...(['DateDebut', 'DateFin'] as const).map<GridColDef<ImportRow>>((field) => ({
        field,
        headerName: t(field === 'DateDebut' ? 'actionImport.fields.startDate' : 'actionImport.fields.endDate'),
        type: 'date',
        width: 150,
        editable: true,
        valueGetter: ({ row }) => parseDateValue(row[field]),
        valueSetter: ({ row, value }) => ({ ...row, [field]: value ? toIsoDay(value as Date) : '' }),
        renderCell: cell(field, (r) =>
          r[field] ? (parseDateValue(r[field]) ? formatTableDate(r[field], i18n.language) : r[field]) : empty
        ),
      })),
      {
        field: 'Cout',
        headerName: t('actionCreate.sections.planning.cost'),
        width: 150,
        align: 'left',
        headerAlign: 'left',
        editable: true,
        valueGetter: ({ row }) => toNumber(row.Cout),
        valueSetter: ({ row, value }) => ({ ...row, Cout: value === null || value === undefined ? '' : String(value) }),
        renderEditCell: (params) => <NumberEditCell {...params} min={0} />,
        renderCell: cell('Cout', (r) =>
          r.Cout === '' ? empty : (
            <span className="incyia-money">{toNumber(r.Cout) === null ? r.Cout : fmt.money(r.Cout)}</span>
          )
        ),
      },
      {
        field: 'Priority',
        headerName: t('actionCreate.sections.planning.priority'),
        type: 'singleSelect',
        width: 130,
        editable: true,
        valueOptions: [1, 2, 3, 4].map((p) => ({ value: p, label: t(`priority.${p}`) })),
        valueGetter: ({ row }) => row.Priority ?? '',
        valueSetter: ({ row, value }) => ({ ...row, Priority: value ? Number(value) : null }),
        renderCell: cell('Priority', (r) => (r.Priority ? t(`priority.${r.Priority}`) : r.raw.Priority || empty)),
      },
      {
        field: 'TypeAction',
        headerName: t('actionCreate.sections.planning.type'),
        type: 'singleSelect',
        width: 150,
        editable: true,
        valueOptions: TypeAction.map((v) => ({ value: v, label: t(TypeActionTranslations[v]) })),
        renderCell: cell('TypeAction', (r) =>
          r.TypeAction ? t(TypeActionTranslations[r.TypeAction] || r.TypeAction) : r.raw.TypeAction || empty
        ),
      },
      {
        field: 'Status',
        headerName: t('actionCreate.sections.planning.status'),
        type: 'singleSelect',
        width: 130,
        editable: true,
        valueOptions: STATUS_VALUES.map((v) => ({ value: v, label: t(v) })),
        renderCell: cell('Status', (r) => (r.Status ? t(r.Status) : r.raw.Status || empty)),
      },
      {
        field: 'MissionId',
        headerName: t('actionCreate.sections.origin.mission'),
        width: 170,
        sortable: false,
        renderCell: cell('MissionId', (r) => labelOf(r.MissionId) || empty),
      },
      {
        field: 'ProjetId',
        headerName: t('actionCreate.sections.origin.project'),
        width: 170,
        sortable: false,
        renderCell: cell('ProjetId', (r) => {
          const sources = r.SourceAudit.length + r.SourceIncident.length + r.SourceRisque.length
          return (
            <>
              {labelOf(r.ProjetId) || empty}
              {sources ? (
                <span className="action-import-chip">{t('actionImport.review.sources', { count: sources })}</span>
              ) : null}
            </>
          )
        }),
      },
      {
        field: 'Security',
        headerName: t('actionCreate.sections.security.title'),
        width: 230,
        sortable: false,
        renderCell: cell('Security', (r) =>
          r.SecurityIds.length ? (
            [r.SecurityIds[0], r.SecurityControlIds[0], r.SubDomainIds[0]]
              .filter(Boolean)
              .map((id) => labelOf(id))
              .join(' › ')
          ) : (
            empty
          )
        ),
      },
      {
        field: '__actions',
        headerName: '',
        width: 92,
        sortable: false,
        align: 'right',
        renderCell: ({ row }) => (
          <span className="action-import-row-actions">
            <button
              type="button"
              className="action-import-icon-btn"
              onClick={() => setEditingId(row.id)}
              aria-label={t('actionImport.review.editRowN', { name: row.ActionName || row.sourceLine || '' })}
              title={t('actionImport.review.editRow')}
            >
              <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
                <path d="M10.8 2.8l2.4 2.4-7.6 7.6-3 .6.6-3z" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="action-import-icon-btn is-danger"
              onClick={() => onDeleteRows([row.id])}
              aria-label={t('actionImport.review.deleteRowN', { name: row.ActionName || row.sourceLine || '' })}
              title={t('actionImport.review.deleteRow')}
            >
              <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
                <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, fmt, cell, errorsById, labels, onDeleteRows]
  )

  const processRowUpdate = useCallback(
    (next: ImportRow, prev: ImportRow) => {
      const editable: (keyof ImportRow)[] = [
        'ActionName',
        'Description',
        'DateDebut',
        'DateFin',
        'Cout',
        'Priority',
        'TypeAction',
        'Status',
      ]
      const patch: Partial<ImportRow> = {}
      const raw = { ...prev.raw }
      editable.forEach((field) => {
        if (next[field] !== prev[field]) {
          ;(patch as any)[field] = typeof next[field] === 'string' ? (next[field] as string) : next[field]
          delete raw[field as keyof typeof raw]
        }
      })
      if (Object.keys(patch).length) onUpdateRow(prev.id, { ...patch, raw })
      return { ...next, raw }
    },
    [onUpdateRow]
  )

  const jumpToNextError = () => {
    if (filter === 'valid') setFilter('all')
    const ids = (apiRef.current?.getSortedRowIds?.() ?? visibleRows.map((r) => r.id)) as string[]
    const errorIds = ids.filter((id) => Object.keys(errorsById[id] || {}).length)
    if (!errorIds.length) return
    const currentIndex = jumpedId ? ids.indexOf(jumpedId) : -1
    const nextId = errorIds.find((id) => ids.indexOf(id) > currentIndex) || errorIds[0]
    const index = ids.indexOf(nextId)
    const page = Math.floor(index / pagination.pageSize)
    setPagination((p) => ({ ...p, page }))
    setJumpedId(nextId)
    const firstField = Object.keys(errorsById[nextId] || {})[0] as RowField | undefined
    const column = firstField ? FIELD_COLUMN[firstField] : 'ActionName'
    window.setTimeout(() => {
      const colIndex = apiRef.current?.getColumnIndex?.(column) ?? 0
      apiRef.current?.scrollToIndexes({ rowIndex: index, colIndex })
      window.setTimeout(() => {
        try {
          apiRef.current?.setCellFocus(nextId, column)
        } catch {
          /* cell not rendered yet */
        }
      }, 60)
    }, 30)
  }

  if (extraction.status === 'loading' || extraction.status === 'idle') {
    return (
      <div className="action-import-state" role="status" aria-live="polite">
        <CircularProgress size={34} sx={{ color: '#076B5C' }} />
        <h4>{t('actionImport.review.extracting')}</h4>
        <p>{t('actionImport.review.extractingHint')}</p>
      </div>
    )
  }

  if (extraction.status === 'error') {
    return (
      <div className="action-import-state is-error" role="alert">
        <h4>{t('actionImport.review.extractFailed')}</h4>
        <p>{extraction.error}</p>
        <DetailButton onClick={onRetryExtract}>{t('actionImport.review.retry')}</DetailButton>
      </div>
    )
  }

  const rowIds = new Set(rows.map((r) => r.id))
  const selectedIds = (selection as string[]).filter((id) => rowIds.has(id))
  const editingRow = editingId ? rows.find((r) => r.id === editingId) : undefined

  return (
    <div className="action-import-review">
      {rows.length ? (
        errorCount ? (
          <div className="action-import-banner is-warning" role="status">
            <span>
              <b>{t('actionImport.review.summaryErrors', { count: errorCount })}</b>{' '}
              {t('actionImport.review.summaryTotal', { total: rows.length, ready: rows.length - errorCount })}
            </span>
            <DetailButton size="sm" variant="ghost" onClick={jumpToNextError}>
              {t('actionImport.review.nextError')}
            </DetailButton>
          </div>
        ) : (
          <div className="action-import-banner is-success" role="status">
            {t('actionImport.review.allReady', { count: rows.length })}
          </div>
        )
      ) : null}

      {budgets.status === 'loading' && rows.some((row) => row.ProjetId) ? (
        <div className="action-import-banner is-busy" role="status">
          <CircularProgress size={16} sx={{ color: '#076B5C' }} aria-hidden />
          <span>{t('actionImport.review.checkingBudgets')}</span>
        </div>
      ) : null}

      {budgetOverruns.map(({ id, total, info }) => (
        <div key={id} className="action-import-banner is-warning" role="status">
          <span>
            <b>{t('actionImport.review.budgetOver', { project: info.projet.ProjetName || labels[id] })}</b>{' '}
            {t('actionImport.review.budgetOverDetail', {
              planned: fmt.money(total),
              remaining: fmt.money(info.remaining),
              budget: fmt.money(info.budget),
            })}
          </span>
        </div>
      ))}

      {deletedCount ? (
        <div className="action-import-banner is-info" role="status">
          <span>{t('actionImport.review.deleted', { count: deletedCount })}</span>
          <DetailButton size="sm" variant="ghost" onClick={onUndoDelete}>
            {t('actionImport.review.undo')}
          </DetailButton>
        </div>
      ) : null}

      <div className="action-import-toolbar">
        <input
          className="incyia-form-inp action-import-search"
          type="search"
          placeholder={t('actionImport.review.search')}
          aria-label={t('actionImport.review.search')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPagination((p) => ({ ...p, page: 0 }))
          }}
        />
        <div className="action-import-segment" role="group" aria-label={t('actionImport.review.filter')}>
          {(['all', 'valid', 'errors'] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              className={filter === f ? 'is-active' : ''}
              onClick={() => {
                setFilter(f)
                setPagination((p) => ({ ...p, page: 0 }))
              }}
            >
              {t(`actionImport.review.filters.${f}`)}
              <span className="action-import-segment-count">
                {f === 'all' ? rows.length : f === 'valid' ? rows.length - errorCount : errorCount}
              </span>
            </button>
          ))}
        </div>
        <span className="action-import-toolbar-spacer" />
        {selectedIds.length ? (
          <div className="action-import-selection" role="status">
            <span>{t('actionImport.review.selected', { count: selectedIds.length })}</span>
            <DetailButton size="sm" variant="ghost" onClick={() => setBulkOpen(true)}>
              {t('actionImport.review.bulkEdit')}
            </DetailButton>
            <DetailButton
              size="sm"
              variant="danger"
              onClick={() => {
                onDeleteRows(selectedIds)
                setSelection([])
              }}
            >
              {t('actionImport.review.bulkDelete')}
            </DetailButton>
            <DetailButton size="sm" variant="ghost" onClick={() => setSelection([])}>
              {t('actionImport.review.clearSelection')}
            </DetailButton>
          </div>
        ) : null}
        <DetailButton size="sm" onClick={onAddRow}>
          + {t('actionImport.review.addRow')}
        </DetailButton>
      </div>

      <div
        className="action-import-grid"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && (e.target as HTMLElement).closest('.MuiDataGrid-cell--editing')) {
            e.stopPropagation()
          }
        }}
      >
        <DataGrid
          apiRef={apiRef}
          rows={visibleRows}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          keepNonExistentRowsSelected
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={PAGE_SIZES}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={() => undefined}
          getRowClassName={({ row }) =>
            [
              Object.keys(errorsById[row.id] || {}).length ? 'is-error-row' : '',
              row.id === jumpedId ? 'is-jumped' : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
          rowHeight={56}
          columnHeaderHeight={50}
          disableColumnMenu
          localeText={{
            noRowsLabel: rows.length ? t('actionImport.review.noMatch') : t('actionImport.review.noRows'),
            footerRowSelected: (count) => t('actionImport.review.selected', { count }),
            MuiTablePagination: { labelRowsPerPage: t('actionImport.review.rowsPerPage') },
          }}
          sx={{
            border: 'none',
            fontSize: 15,
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: 13.5, color: '#5b6b87' },
          }}
        />
      </div>

      {editingRow ? (
        <RowEditPanel
          key={editingRow.id}
          row={editingRow}
          onLabels={onLabels}
          lockMission={lockMission}
          lockProject={lockProject}
          onClose={() => setEditingId(null)}
          onSave={(patch) => {
            onUpdateRow(editingRow.id, patch)
            setEditingId(null)
          }}
        />
      ) : null}

      {bulkOpen ? (
        <BulkEditPanel
          count={selectedIds.length}
          onLabels={onLabels}
          lockMission={lockMission}
          lockProject={lockProject}
          preset={lockMission || lockProject ? preset : undefined}
          onClose={() => setBulkOpen(false)}
          onApply={(patch) => {
            onPatchRows(selectedIds, patch)
            setBulkOpen(false)
          }}
        />
      ) : null}
    </div>
  )
}
